import { CircleNotchIcon } from '@phosphor-icons/react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

interface UnsavedDescriptionDialogProps {
  open: boolean
  /** Dismiss (keep editing / stay). */
  onKeepEditing: () => void
  /** Leave without saving the description edit. */
  onDiscard: () => void
  /** Persist the description edit, then leave. */
  onSaveAndClose: () => void
  /** Whether the save is currently in flight (shows a spinner, disables actions). */
  isSaving?: boolean
}

/**
 * Shared "unsaved description edit" confirmation. Used both by the per-panel
 * close/route guard and the app-level task-switch guard so the copy and layout
 * stay identical. Dismissal (Escape/overlay) is treated as "keep editing".
 */
export function UnsavedDescriptionDialog({
  open,
  onKeepEditing,
  onDiscard,
  onSaveAndClose,
  isSaving = false,
}: UnsavedDescriptionDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={next => {
        // Don't allow dismissal mid-save; otherwise treat it as "keep editing".
        if (!next && !isSaving) onKeepEditing()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            Your description edit hasn&apos;t been saved. What would you like to
            do?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onKeepEditing} disabled={isSaving}>
            Keep editing
          </Button>
          <Button variant="ghost" onClick={onDiscard} disabled={isSaving}>
            Discard
          </Button>
          <Button onClick={onSaveAndClose} disabled={isSaving}>
            {isSaving ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save & close'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
